<template>
    <div class="pwa-planning-day">
        <p class="text-base font-bold underline capitalize mb-1 leading-none">
            {{ $moment(day).format('dddd DD MMMM YYYY') }}
        </p>
        <p class="text-sm leading-none font-normal" v-if="totalDayTime.total">
            {{ totalDayTime.schedules ? totalDayTime.schedules.start + ' à ' + totalDayTime.schedules.end + ' (' + totalDayTime.total + ')' : '' }}
        </p>
        <div class="md-layout md-gutter">
            <div class="md-layout-item md-size-100">
                <template v-if="absences.length > 0">
                    <div v-for="absence in absences" :key="absence.id"
                         class="planningable planningable-absence" :class="'bg-absence-' + absence.absence_type_id">
                        <span class="font-medium text-black">Absence prévue : {{ absence.absence_type.name }}</span>
                    </div>
                </template>
                <template v-for="(planningable, index) in plannings">
                    <div v-if="isIntervention(planningable)" class="planningable" :key="planningable.id"
                         :class="{'planningable-with-sector': showSector(index, planningable)}">
                        <div v-if="showSector(index, planningable)" class="mb-1">
                            <span class="font-medium underline">{{ site(planningable.intervention).sector.name }}</span>
                        </div>
                        <div class="planningable-intervention cursor-pointer" :class="colorIntervention(planningable.intervention)"
                            @click="goToRoute('Pwa Intervention Details', { interventionId: planningable.intervention.id })">
                            <div>
                                <span class="font-medium">{{ schedule(planningable).start + ' / ' + schedule(planningable).end }}</span>
                            </div>
                            <div>
                                <span class="font-medium">Client / Site :</span>
                                <span class="ml-1">{{ site(planningable.intervention).client.name + ' : ' + site(planningable.intervention).name }}</span>
                            </div>
	                        <div>
		                        <span class="font-medium">Type :</span>
		                        <span class="ml-1">{{ planningable.intervention.intervention_type.name }}</span>
	                        </div>
                            <template v-if="planningable.intervention.status_id !== 6">
                                <div v-if="planningable.intervention.comment">
                                    <span class="font-medium">Informations :</span>
                                    <span class="ml-1">{{ planningable.intervention.comment }}</span>
                                </div>
                                <div v-if="site(planningable.intervention).is_baiting_unpermanent">
                                    <span class="font-medium text-danger">{{ site(planningable.intervention).is_smart ? 'SMART' : 'Appatage non permanent' }}</span>
                                </div>
                            </template>
                        </div>
                        <div v-if="showTravelTime(index, planningable)" class="planningable planningable-travel">
                            <span class="font-medium">Trajet : {{ travelTime(planningable, plannings[index + 1]) }}</span>
                        </div>
                    </div>
                    <template v-else-if="isElement(planningable)">
                        <div v-if="planningable.element.is_pause" :key="planningable.id"
                             class="planningable planningable-element planningable-lunch">
                            <div class="flex items-center">
                                <div class="font-medium">
                                    <span>{{ planningable.element.text }}</span>
                                    <span class="ml-1">- {{ durationBetweenDates(planningable.start_date, planningable.end_date) }}</span>
                                </div>
                            </div>
                        </div>
                        <div v-else-if="planningable.element.is_deplacement" :key="planningable.id"
                             class="planningable planningable-element planningable-deplacement">
                            <div class="flex items-center justify-between">
                                <div class="font-medium pr-1.5">
                                    <span>{{ planningable.element.text }}</span>
                                    <span class="ml-1">- {{ durationBetweenDates(planningable.start_date, planningable.end_date) }}</span>
                                </div>
                            </div>
                        </div>
                        <div v-else :key="planningable.id" class="planningable planningable-element">
                            <div class="flex items-center justify-between">
                                <div class="font-medium pr-1.5">
                                    <span>{{ schedule(planningable).start + ' / ' + schedule(planningable).end }}</span>
                                    <span class="ml-1">- {{ planningable.element.text }}</span>
                                </div>
                            </div>
                        </div>
                    </template>
                    <div v-else-if="planningable.absence_type_id" :key="planningable.id"
                         class="planningable planningable-element planningable-absence">
                        <div class="flex items-center justify-between">
                            <div class="font-medium">
                                <span>{{ schedule(planningable).start + ' / ' + schedule(planningable).end }}</span>
                                <span class="ml-1">- Réunion {{ planningable.comment ? ' : ' + planningable.comment : '' }}</span>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-if="plannings.length === 0 && absences.length === 0">
                    <p class="text-sm text-primary font-normal mb-2">Aucun évènement</p>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import dateTimeFormattingMixin from "@/mixins/date-time-formatting-mixin";

export default {
    name: "PlanningDay",
    mixins: [dateTimeFormattingMixin],
    props: {
        plannings: Array,
        day: String,
        absences: Array,
    },
    computed: {
        totalDayTime() {
            let total = 0
            this.plannings.map((planningable) => {
                if (planningable.end_date) {
                    total += (this.$moment.duration(this.$moment(planningable.end_date).diff(this.$moment(planningable.start_date)))).asMinutes()
                } else if (planningable.date_end) {
                    total += (this.$moment.duration(this.$moment(planningable.date_end).diff(this.$moment(planningable.date_start)))).asMinutes()
                }
            })

            let firstElement = this.plannings.find(planningable => this.$moment(planningable.start_date).format('HH') !== '00') ?? {}
            let lastElement = this.plannings.find((planningable, index) =>
                (index === (this.plannings.length - 1) && (planningable.end_date || planningable.date_end))
            )

            return {
                total: total > 0 ? this.hoursAndMinutes(total) : null,
                schedules: this.plannings.length > 0 ? {
                    start: this.$moment(firstElement.start_date).format('HH:mm'),
                    end: lastElement.end_date ? this.$moment(lastElement.end_date).format('HH:mm') : this.$moment(lastElement.date_end).format('HH:mm'),
                } : null
            }
        },
    },
    methods: {
        isIntervention(planningable) {
            return planningable && planningable.planningable_type ? planningable.planningable_type.includes('Intervention') && planningable.intervention !== null : false
        },
        isElement(planningable) {
            return planningable && planningable.planningable_type ? planningable.planningable_type.includes('Element') && planningable.element !== null : false
        },
        searchPreviousIntervention(indexCurrentIntervention) {
            let index = 1
            let previousPlanning = this.plannings[indexCurrentIntervention - index]
            while (!previousPlanning.intervention && (indexCurrentIntervention - index >= 0)) {
                index++
                previousPlanning = this.plannings[indexCurrentIntervention - index]
            }
            return previousPlanning
        },
        showSector(index, planningable) {
            return ((!this.plannings[(index - 1)] || !this.plannings[(index - 1)].intervention) ||
                (this.plannings[(index - 1)]
                    && !this.sameSector(this.searchPreviousIntervention(index).intervention, planningable.intervention)
                )
            )
        },
        sameSector(previousIntervention, currentIntervention) {
            let previousSector = this.site(previousIntervention).sector
            let currentSector = this.site(currentIntervention).sector
            if (!previousSector || !currentSector) {
                return false
            } else {
                return previousSector.id === currentSector.id
            }
        },
        site(intervention) {
            let site = {name: 'Pas de site', id: '', address: {}, sector: {}}
            if (intervention.site) {
                site = intervention.site ?? {id: null, name: 'Pas de site', address: {}, sector: {}}
            } else {
                let contract = intervention?.planning_intervention_prevision?.planning_intervention_group?.planning_intervention?.contract
                site = contract.site ?? {id: null, name: 'Pas de site', address: {}, sector: {}}
            }
            site.sector = site.sector ?? {name: 'Pas de secteur'}
            return site
        },
        sameSite(nextPlanningable, currentIntervention) {
            if (nextPlanningable.intervention) {
                const nextSite = this.site(nextPlanningable.intervention)
                const currentSite = this.site(currentIntervention)
                if (!nextSite || !currentSite) {
                    return false
                } else {
                    return nextSite.id === currentSite.id
                }
            } else {
                return false
            }
        },
        showTravelTime(index, planningable) {
            return index !== (this.plannings.length - 1) && !this.isElement(this.plannings[index + 1])
                && !this.sameSite(this.plannings[index + 1], planningable.intervention)
        },
        travelTime(currentPlanningable, nextPlanningable) {
            const duration = (this.$moment.duration(this.$moment(nextPlanningable.start_date).diff(this.$moment(currentPlanningable.end_date)))).asMinutes()
            const time = this.hoursAndMinutes(duration)
            return time.includes('-') ? 'Invalide' : time
        },
        schedule(planningable) {
            const startDate = planningable.start_date ?? planningable.date_start
            const endDate = planningable.end_date ?? planningable.date_end
            return {
                start: this.$moment(startDate).format('HH:mm'),
                end: this.$moment(endDate).format('HH:mm')
            }
        },
        goToRoute(route, params = {}) {
            this.$router.push({name: route, params})
        },
        colorIntervention(intervention) {
            if(intervention.status_id === 6) {
                return 'intervention-success'
            } else {
                return {
                    'intervention-blue': intervention.category_id === 1,
                    'intervention-purple': intervention.category_id === 2 || intervention.category_id === 3,
                    'intervention-orange': intervention.category_id === 4 || intervention.category_id === 5,
                }
            }
        }
    }
}
</script>

<style scoped lang="scss">
.pwa-planning-day {
    &:first-child {
        margin-top: 1rem;
    }

    .planningable {
        text-align: left;
        font-size: .85rem;
        line-height: 1.15rem;
        margin: .5rem 0;

        .planningable-intervention {
            padding: .25rem .5rem;
            border-radius: 3px;
            background-color: $white;
            border-left: 4px solid white;
	        color: #000000;
            //border-top: 5px solid white;

            &.intervention-success {
                border-color: $green-800;
                background-color: $green-200;
            }

            &.intervention-blue {
                border-color: $blue-800;
                background-color: $blue-100;
            }

            &.intervention-purple {
                border-color: $purple-800;
                background-color: $purple-100;
            }

            &.intervention-orange {
                border-color: $orange-800;
                background-color: $orange-100;
            }
        }

        &.planningable-element {
            color: #FFFFFF;
            background-color: $brand-primary;
            border-radius: 3px;
            padding: .25rem .5rem;
            margin-top: .25rem;

            &.planningable-absence {
                background-color: $pink-200;
            }

            &.planningable-lunch {
                background-color: $green-200;
            }

            &.planningable-deplacement {
                background-color: $cyan-200;
            }
        }

        &.planningable-absence {
            border-radius: 3px;
            padding: .25rem .5rem;
            margin-top: .5rem;
            color: #FFFFFF;

            &.bg-absence-1 {
                background: $gray-lighter-2;
            }
            &.bg-absence-2 {
                background: $cyan-100;
            }
            &.bg-absence-3 {
                background: $orange-100;
            }
            &.bg-absence-4 {
                background: $green-100;
            }
            &.bg-absence-5 {
                background: $red-200;
            }
            &.bg-absence-6 {
                background: $purple-100;
            }
        }

        &.planningable-travel {
            margin-bottom: 0 !important;
            background-color: $white;
            padding: .25rem .5rem;
            border-radius: 3px;
            border-left: 4px solid $black;
        }

        &.planningable-with-sector {
            margin-top: .25rem;
            &:first-child {
                margin-top: 0 !important;
            }
        }

        &:last-child {
            margin-bottom: 15px;
        }
    }
}
</style>
